/**
 * WebAuthnService — canonical browser WebAuthn API wrapper for passkeys.
 *
 * Standards-ID: MFA-FACTORY-001 (PLATFORM-PASSKEY-001 promotion, 2026-06-06).
 *
 * MFA-FACTORY-001 established `webAuthnService` as the app-provided DI boundary
 * for the canonical `<PasskeyManagement>` UI + `usePasskeyManagement` hook. The
 * implementation was byte-identical across purchase-web, palm-web, patrol-web,
 * payday-web, and planner-web; this promotes it to the package so new consumers
 * (the Platform Console) reuse the shared primitive instead of forking a 6th
 * copy. The five per-app copies migrate to this export under MFA-FACTORY-002.
 *
 * Provides base64url encoding/decoding, credential creation/retrieval, and
 * server-option preparation for FIDO2/WebAuthn flows. Stateless — safe to
 * instantiate per-consumer.
 *
 * @module @puffin-core/mfa-ui/WebAuthnService
 */

/**
 * Convert a base64url-encoded string to a Uint8Array.
 * @param {string} base64url - Base64url-encoded string
 * @returns {Uint8Array} Decoded bytes
 */
export function base64urlToUint8Array(base64url) {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const pad = base64.length % 4;
  const padded = pad ? base64 + '='.repeat(4 - pad) : base64;
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Convert an ArrayBuffer to a base64url-encoded string.
 * @param {ArrayBuffer} buffer - Buffer to encode
 * @returns {string} Base64url-encoded string
 */
export function arrayBufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * WebAuthn Service for passkey authentication.
 */
export class WebAuthnService {
  /**
   * Check if WebAuthn is supported in the browser.
   * @returns {Promise<boolean>} Support status
   */
  async isSupported() {
    return Boolean(
      window.PublicKeyCredential &&
      navigator.credentials &&
      navigator.credentials.create &&
      navigator.credentials.get,
    );
  }

  /**
   * Check if a platform authenticator is available (Touch ID, Windows Hello, …).
   * @returns {Promise<boolean>} Platform authenticator availability
   */
  async hasPlatformAuthenticator() {
    if (!window.PublicKeyCredential) {
      return false;
    }
    // No catch (SWALLOWED-CATCH-BURNDOWN-001 U3): the method's promise RESOLVES a boolean and has
    // no rejection step (W3C WebAuthn: resolve true when one is found, otherwise false), so the old
    // catch could only turn a defect into "no platform authenticator".
    return window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  }

  /**
   * Check if conditional mediation (autofill) is available.
   * @returns {Promise<boolean>} Conditional mediation availability
   */
  async hasConditionalMediation() {
    if (
      !window.PublicKeyCredential ||
      !window.PublicKeyCredential.isConditionalMediationAvailable
    ) {
      return false;
    }
    // No catch (U3): resolves a boolean, no rejection step (W3C WebAuthn).
    return window.PublicKeyCredential.isConditionalMediationAvailable();
  }

  /**
   * Create a new credential (registration). Wraps
   * `navigator.credentials.create({ publicKey: options })`.
   * @param {Object} options - PublicKey credential creation options
   * @returns {Promise<PublicKeyCredential>} Created credential
   */
  async createCredential(options) {
    return navigator.credentials.create({ publicKey: options });
  }

  /**
   * Get a credential (authentication). Wraps
   * `navigator.credentials.get({ publicKey: options })`.
   * @param {Object} options - PublicKey credential request options
   * @returns {Promise<PublicKeyCredential>} Retrieved credential
   */
  async getCredential(options) {
    return navigator.credentials.get({ publicKey: options });
  }

  /**
   * Prepare server registration options for the browser WebAuthn API.
   * Converts base64url-encoded fields to Uint8Array for the browser.
   * @param {Object} options - Server-provided registration options
   * @returns {Object} Browser-ready publicKey options
   */
  prepareRegistrationOptions(options) {
    const prepared = {
      ...options,
      challenge: base64urlToUint8Array(options.challenge),
      user: {
        ...options.user,
        id: base64urlToUint8Array(options.user.id),
      },
    };

    if (options.excludeCredentials && options.excludeCredentials.length > 0) {
      prepared.excludeCredentials = options.excludeCredentials.map((cred) => ({
        ...cred,
        id: base64urlToUint8Array(cred.id),
      }));
    }

    return prepared;
  }

  /**
   * Prepare server authentication options for the browser WebAuthn API.
   * Converts base64url-encoded challenge to Uint8Array.
   * @param {Object} options - Server-provided authentication options
   * @returns {Object} Browser-ready publicKey options
   */
  prepareAuthenticationOptions(options) {
    const prepared = {
      ...options,
      challenge: base64urlToUint8Array(options.challenge),
    };

    if (options.allowCredentials && options.allowCredentials.length > 0) {
      prepared.allowCredentials = options.allowCredentials.map((cred) => ({
        ...cred,
        id: base64urlToUint8Array(cred.id),
      }));
    }

    return prepared;
  }

  /**
   * Format a registration credential response for the server.
   * Converts ArrayBuffer fields to base64url strings.
   * @param {PublicKeyCredential} credential - Browser credential from create()
   * @returns {Object} JSON-serializable credential for server
   */
  formatRegistrationResponse(credential) {
    return {
      id: credential.id,
      rawId: arrayBufferToBase64url(credential.rawId),
      type: credential.type,
      response: {
        attestationObject: arrayBufferToBase64url(credential.response.attestationObject),
        clientDataJSON: arrayBufferToBase64url(credential.response.clientDataJSON),
      },
    };
  }

  /**
   * Format an authentication assertion response for the server.
   * Converts ArrayBuffer fields to base64url strings.
   * @param {PublicKeyCredential} credential - Browser credential from get()
   * @returns {Object} JSON-serializable assertion for server
   */
  formatAuthenticationResponse(credential) {
    return {
      id: credential.id,
      rawId: arrayBufferToBase64url(credential.rawId),
      type: credential.type,
      response: {
        authenticatorData: arrayBufferToBase64url(credential.response.authenticatorData),
        clientDataJSON: arrayBufferToBase64url(credential.response.clientDataJSON),
        signature: arrayBufferToBase64url(credential.response.signature),
        userHandle: credential.response.userHandle
          ? arrayBufferToBase64url(credential.response.userHandle)
          : null,
      },
    };
  }
}

export default WebAuthnService;
